import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  getAuth,
  signOut,
  updatePassword,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase/config";
import { doc, updateDoc } from "firebase/firestore";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaCreditCard,
  FaSignOutAlt,
  FaCheck,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { getFunctions, httpsCallable } from "firebase/functions";
import GrowthStats from "../components/GrowthStats";

export default function Account({ theme, switchTheme }) {
  const { currentUser, userId, subscriptionTier } = useAuth();
  const auth = getAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Account Settings - ASites Planner";
  }, []);

  const openPortal = async () => {
    const functions = getFunctions();
    const createPortal = httpsCallable(functions, "createCustomerPortal");

    const res = await createPortal();
    window.location.href = res.data.url;
  };

  // Profile State
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  // Password State
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI State
  const [message, setMessage] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState({
    profile: false,
    password: false,
  });
  const [activeSection, setActiveSection] = useState("profile");

  useEffect(() => {
    if (currentUser) {
      setName(currentUser.displayName || "");
      setEmail(currentUser.email || "");
    }
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/login";
    } catch (error) {
      console.error("Error logging out:", error);
      setMessage({
        text: "Error logging out. Please try again.",
        type: "error",
      });
    }
  };

  // Update Profile
  const saveProfile = async () => {
    if (!currentUser || !name.trim()) return;

    setLoading((prev) => ({ ...prev, profile: true }));
    setMessage({ text: "", type: "" });

    try {
      // Update auth displayName
      await updateProfile(currentUser, {
        displayName: name.trim(),
      });

      // Update firestore profile
      if (userId) {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, {
          name: name.trim(),
          updatedAt: new Date(),
        });
      }

      setMessage({
        text: "Profile updated successfully!",
        type: "success",
      });
      setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    } catch (err) {
      console.error("Profile update error:", err);
      setMessage({
        text: "Failed to update profile. Please try again.",
        type: "error",
      });
    } finally {
      setLoading((prev) => ({ ...prev, profile: false }));
    }
  };

  // Change Password
  const changePassword = async () => {
    if (!currentUser) return;

    // Validation
    if (!pwCurrent) {
      setMessage({
        text: "Please enter your current password",
        type: "error",
      });
      return;
    }

    if (!pwNew || pwNew.length < 6) {
      setMessage({
        text: "New password must be at least 6 characters",
        type: "error",
      });
      return;
    }

    if (pwNew !== pwConfirm) {
      setMessage({
        text: "New passwords do not match",
        type: "error",
      });
      return;
    }

    setLoading((prev) => ({ ...prev, password: true }));
    setMessage({ text: "", type: "" });

    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        pwCurrent
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, pwNew);

      // Clear password fields
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");

      setMessage({
        text: "Password updated successfully!",
        type: "success",
      });
      setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    } catch (err) {
      console.error("Password change error:", err);

      let errorMessage = "Failed to change password. ";
      if (err.code === "auth/wrong-password") {
        errorMessage += "Current password is incorrect.";
      } else if (err.code === "auth/requires-recent-login") {
        errorMessage += "Please re-login and try again.";
      } else {
        errorMessage += "Please try again.";
      }

      setMessage({ text: errorMessage, type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, password: false }));
    }
  };

  if (!currentUser) {
    return (
      <Container>
        <Card>
          <Title>Account</Title>
          <EmptyState>
            <FaUser size={48} />
            <h3>Not Logged In</h3>
            <p>Please log in to view your account details.</p>
            <LoginButton href="/login">Go to Login</LoginButton>
          </EmptyState>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Header>
          <Title>Account Settings</Title>
          <Subtitle>Manage your account and subscription</Subtitle>
        </Header>

        <Navigation>
          <NavButton
            $active={activeSection === "profile"}
            onClick={() => setActiveSection("profile")}
          >
            <FaUser />
            Profile
          </NavButton>
          <NavButton
            $active={activeSection === "security"}
            onClick={() => setActiveSection("security")}
          >
            <FaLock />
            Security
          </NavButton>
          <NavButton
            $active={activeSection === "subscription"}
            onClick={() => setActiveSection("subscription")}
          >
            <FaCreditCard />
            Subscription
          </NavButton>
        </Navigation>

        {message.text && (
          <MessageBanner $type={message.type}>
            {message.type === "success" ? (
              <FaCheck />
            ) : (
              <FaExclamationTriangle />
            )}
            <span>{message.text}</span>
          </MessageBanner>
        )}

        {activeSection === "profile" && (
          <Section>
            <SectionHeader>
              <SectionIcon>
                <FaUser />
              </SectionIcon>
              <SectionTitle>Profile Information</SectionTitle>
            </SectionHeader>

            <InputGroup>
              <InputLabel>
                <FaUser size={14} />
                Full Name
              </InputLabel>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>
                <FaEnvelope size={14} />
                Email Address
              </InputLabel>
              <EmailDisplay>
                {email}
                <EmailNote>Email cannot be changed</EmailNote>
              </EmailDisplay>
            </InputGroup>

            <ButtonGroup>
              <PrimaryButton
                onClick={saveProfile}
                disabled={loading.profile || !name.trim()}
              >
                {loading.profile ? "Saving..." : "Save Changes"}
              </PrimaryButton>
            </ButtonGroup>
          </Section>
        )}

        {activeSection === "security" && (
          <Section>
            <SectionHeader>
              <SectionIcon>
                <FaLock />
              </SectionIcon>
              <SectionTitle>Change Password</SectionTitle>
            </SectionHeader>

            <InputGroup>
              <InputLabel>
                <FaLock size={14} />
                Current Password
              </InputLabel>
              <PasswordInput>
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                  placeholder="Enter current password"
                />
                <PasswordToggle
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                </PasswordToggle>
              </PasswordInput>
            </InputGroup>

            <InputGroup>
              <InputLabel>
                <FaLock size={14} />
                New Password
              </InputLabel>
              <PasswordInput>
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  placeholder="Enter new password (min. 6 characters)"
                />
                <PasswordToggle
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </PasswordToggle>
              </PasswordInput>
            </InputGroup>

            <InputGroup>
              <InputLabel>
                <FaLock size={14} />
                Confirm New Password
              </InputLabel>
              <PasswordInput>
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  placeholder="Confirm new password"
                />
                <PasswordToggle
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </PasswordToggle>
              </PasswordInput>
            </InputGroup>

            <PasswordRequirements>
              <Requirement $met={pwNew.length >= 6}>
                At least 6 characters
              </Requirement>
              <Requirement $met={pwNew === pwConfirm && pwNew !== ""}>
                Passwords match
              </Requirement>
            </PasswordRequirements>

            <ButtonGroup>
              <PrimaryButton
                onClick={changePassword}
                disabled={
                  loading.password ||
                  !pwCurrent ||
                  !pwNew ||
                  pwNew !== pwConfirm
                }
              >
                {loading.password ? "Updating..." : "Update Password"}
              </PrimaryButton>
            </ButtonGroup>
          </Section>
        )}

        {activeSection === "subscription" && (
          <Section>
            <SectionHeader>
              <SectionIcon>
                <FaCreditCard />
              </SectionIcon>
              <SectionTitle onClick={openPortal}>
                Subscription Management
              </SectionTitle>
            </SectionHeader>

            <SubscriptionCard>
              <PlanBadge $isPro={subscriptionTier === "PRO"}>
                {subscriptionTier === "PRO" ? "Pro Plan" : "Free Plan"}
              </PlanBadge>

              <PlanFeatures>
                {subscriptionTier === "PRO" ? (
                  <>
                    <Feature>
                      <FaCheck /> Advanced Analytics
                    </Feature>
                    <Feature>
                      <FaCheck /> Custom Quotes
                    </Feature>
                    <Feature>
                      <FaCheck /> Priority Support
                    </Feature>
                    <Feature>
                      <FaCheck /> Unlimited Storage
                    </Feature>
                  </>
                ) : (
                  <>
                    <Feature>
                      <FaCheck /> Basic Features
                    </Feature>
                    <Feature>
                      <FaCheck /> Limited Storage
                    </Feature>
                    <Feature>
                      <FaCheck /> Community Support
                    </Feature>
                    <Feature>
                      <FaLock /> Custom Quotes
                    </Feature>
                  </>
                )}
              </PlanFeatures>
            </SubscriptionCard>

            <SubscriptionActions>
              <PrimaryButton onClick={openPortal}>
                <FaCreditCard />
                Manage Subscription
              </PrimaryButton>
              {subscriptionTier !== "PRO" && (
                <SecondaryButton href="/upgrade">
                  Upgrade to Pro
                </SecondaryButton>
              )}
            </SubscriptionActions>
          </Section>
        )}

        <Divider />

        <FooterActions>
          <LogoutButton onClick={handleLogout}>
            <FaSignOutAlt />
            Log Out
          </LogoutButton>
        </FooterActions>
      </Card>
    </Container>
  );
}

// Styled Components
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  color: var(--fontPrimaryColor);
  background: var(--primaryBackgroundColor);
`;

const Card = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 20px;
  padding: 2.5rem;
  width: 100%;
  max-width: 800px;
  box-shadow: 0 10px 40px var(--shadowColor);
  border: 1px solid var(--shadowColor);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--primaryColor);
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
`;

const Navigation = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--thirdBackgroundColor);
  padding-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${(props) =>
    props.$active ? "var(--primaryColor)" : "transparent"};
  color: ${(props) => (props.$active ? "white" : "var(--fontSecondaryColor)")};
  border: none;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Montserrat", sans-serif;

  &:hover {
    background: ${(props) =>
      props.$active ? "var(--primaryColor)" : "var(--thirdBackgroundColor)"};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const MessageBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  background: ${(props) =>
    props.$type === "success"
      ? "rgba(16, 185, 129, 0.1)"
      : "rgba(239, 68, 68, 0.1)"};
  color: ${(props) => (props.$type === "success" ? "#10b981" : "#ef4444")};
  border: 1px solid
    ${(props) =>
      props.$type === "success"
        ? "rgba(16, 185, 129, 0.2)"
        : "rgba(239, 68, 68, 0.2)"};

  svg {
    font-size: 1.25rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SectionIcon = styled.div`
  color: var(--primaryColor);
  font-size: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 400;
  margin: 0;
  color: var(--fontPrimaryColor);
`;

const InputGroup = styled.div`
  margin-bottom: 1.5rem;
  font-family: "Montserrat", sans-serif;
`;

const InputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: var(--fontPrimaryColor);
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--shadowColor);
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  font-size: 1rem;
  transition: all 0.3s ease;
  font-family: "Montserrat", sans-serif;

  &:focus {
    outline: none;
    border-color: var(--primaryColor);
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PasswordInput = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  font-family: "Montserrat", sans-serif;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: var(--fontSecondaryColor);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--fontPrimaryColor);
  }
`;

const EmailDisplay = styled.div`
  padding: 0.875rem 1rem;
  background: var(--thirdBackgroundColor);
  border-radius: 10px;
  color: var(--fontPrimaryColor);
  font-weight: 500;
  border: 1px solid var(--shadowColor);
`;

const EmailNote = styled.div`
  font-size: 0.75rem;
  color: var(--fontSecondaryColor);
  margin-top: 0.25rem;
`;

const PasswordRequirements = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--thirdBackgroundColor);
  border-radius: 10px;
`;

const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${(props) => (props.$met ? "#10b981" : "var(--fontSecondaryColor)")};

  &:before {
    content: "${(props) => (props.$met ? "✓" : "○")}";
    font-weight: bold;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.875rem 1.5rem;
  background: var(--primaryColor);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Montserrat", sans-serif;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.875rem 1.5rem;
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  border: 1px solid var(--shadowColor);
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: var(--lowPopupBg);
    transform: translateY(-2px);
  }
`;

const SubscriptionCard = styled.div`
  background: var(--lowPopupBg);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--shadowColor);
`;

const PlanBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  background: ${(props) =>
    props.$isPro
      ? "linear-gradient(135deg, #8b5cf6, #6366f1)"
      : "var(--thirdBackgroundColor)"};
  color: ${(props) => (props.$isPro ? "white" : "var(--fontPrimaryColor)")};
  margin-bottom: 1rem;
`;

const PlanFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--fontSecondaryColor);

  svg {
    color: var(--primaryColor);
  }
`;

const SubscriptionActions = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--thirdBackgroundColor);
  margin: 2rem 0;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: center;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.5rem;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    transform: translateY(-2px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--fontSecondaryColor);

  svg {
    margin-bottom: 1rem;
    color: var(--fontSecondaryColor);
  }

  h3 {
    margin-bottom: 0.5rem;
    color: var(--fontPrimaryColor);
  }
`;

const LoginButton = styled.a`
  display: inline-block;
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: var(--primaryColor);
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  }
`;
